import { useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { AppTable } from '../../../components/AppTable';
import { InviteListHook, useDebouncedMemo, UsersListHook } from '../../../hooks';
import { AddUserPopup, InviteRow, UserRow } from '../../../page-components/settings';
import { SettingsUsersToolbar } from '../../../page-components/settings/settings-users-toolbar';
import { CommonUtility, InvitesService, ToastMessage } from '../../../utility';
import { ConfirmationPopup } from '../../../components';
import { useAuth } from '../../../contexts/auth';

const SUB_TAB_USERS = 'users';
const SUB_TAB_INVITATIONS = 'invitations';

export function UsersTab() {
  const [activeSubTab, setActiveSubTab] = useState(SUB_TAB_USERS);
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);

  const { currentOrg } = useAuth();
  const isAdmin = useMemo(() => currentOrg?.role === 'admin', [currentOrg]);

  const { data: userData, loading: userLoading, refresh: refreshUsers } = UsersListHook();

  const {
    data: inviteData,
    loading: inviteLoading,
    refresh: refreshInvites,
  } = InviteListHook();

  const [addUserPopup, setAddUserPopup] = useState(false);
  const [removeUserPopup, setRemoveUserPopup] = useState(null);
  const [removeInvitePopup, setRemoveInvitePopup] = useState(null);

  const columns = [
    {
      text: 'Name',
      key: 'name',
      width: 4,
      textAlign: 'left',
    },
    {
      text: 'Email Address',
      key: 'EmailAddress',
      width: 4,
      textAlign: 'left',
    },
    {
      text: 'Role',
      key: 'role',
      width: 2,
      textAlign: 'left',
    },
    {
      text: '',
      width: 2,
      key: 'Actions',
      textAlign: 'left',
    },
  ];

  const invitationColumns = [
    {
      text: 'Name',
      key: 'name',
      width: 4,
      textAlign: 'left',
    },
    {
      text: 'Email Address',
      key: 'EmailAddress',
      width: 4,
      textAlign: 'left',
    },
    {
      text: 'Role',
      width: 2,
      key: 'role',
      textAlign: 'left',
    },
    {
      text: '',
      width: 2,
      key: 'Actions',
      textAlign: 'left',
    },
  ];

  const openPopup = () => {
    setAddUserPopup(true);
  };

  const closePopup = () => {
    setAddUserPopup(false);
  };

  const removeUser = user => {
    setRemoveUserPopup(user);
  };

  const closeRemoveUserPopup = () => {
    setRemoveUserPopup(null);
  };

  const removeInvite = user => {
    setRemoveInvitePopup(user);
  };
  const closeRemoveInvitePopup = () => {
    setRemoveInvitePopup(null);
  };

  const resendInvite = async inviteId => {
    try {
      setLoading(true);
      await InvitesService.resendInvite(inviteId);
      ToastMessage.success('Invite resent successfully');
      refreshInvites();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDeleteUser = async () => {
    try {
      setPopupLoading(true);
      await InvitesService.removeUser(removeUserPopup?._id);
      ToastMessage.success('User deleted successfully');
      refreshUsers();
      closeRemoveUserPopup();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setPopupLoading(false);
    }
  };
  const onConfirmDeleteInvite = async () => {
    try {
      setPopupLoading(true);
      await InvitesService.remove(removeInvitePopup?.token);
      ToastMessage.success('Invite deleted successfully');
      refreshInvites();
      closeRemoveInvitePopup();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setPopupLoading(false);
    }
  };

  const filteredUsers = useDebouncedMemo(
    () => {
      if (search) {
        return userData?.filter(
          user =>
            CommonUtility.searchQueryCompare(user.firstName, search) ||
            CommonUtility.searchQueryCompare(user.lastName, search) ||
            CommonUtility.searchQueryCompare(user.email, search),
        );
      }
      return userData;
    },
    [userData, search],
    100,
  );

  const filteredInvites = useDebouncedMemo(
    () => {
      if (search) {
        return inviteData?.filter(
          invite =>
            CommonUtility.searchQueryCompare(invite.firstName, search) ||
            CommonUtility.searchQueryCompare(invite.lastName, search) ||
            CommonUtility.searchQueryCompare(invite.email, search),
        );
      }
      return inviteData;
    },
    [inviteData, search],
    100,
  );

  const toggle2FA = async user => {
    try {
      setLoading(true);
      const payload = {
        bypass_2fa: !user.bypass_2fa,
      };
      await InvitesService.updateUser(user._id, payload);
      ToastMessage.success('2FA toggled successfully');
      refreshUsers();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tabs
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
        style={{ width: '100%' }}
        tabBarExtraContent={
          <SettingsUsersToolbar
            search={search}
            onSearchChange={setSearch}
            onAddUser={openPopup}
          />
        }
        items={[
          {
            key: SUB_TAB_USERS,
            label: 'Users',
            children: (
              <AppTable
                data={filteredUsers}
                RowComponent={
                  <UserRow remove={removeUser} toggle2FA={toggle2FA} isAdmin={isAdmin} />
                }
                columns={columns}
                noDataMessage="No user data found"
                loading={userLoading || loading}
                loadingRows={6}
              />
            ),
          },
          {
            key: SUB_TAB_INVITATIONS,
            label: 'Open Invitations',
            children: (
              <AppTable
                data={filteredInvites}
                RowComponent={
                  <InviteRow removeInvite={removeInvite} resendInvite={resendInvite} />
                }
                columns={invitationColumns}
                noDataMessage="No user data found"
                loading={inviteLoading || loading}
              />
            ),
          },
        ]}
      />
      <AddUserPopup refresh={refreshInvites} open={addUserPopup} onClose={closePopup} />
      <ConfirmationPopup
        open={!!removeUserPopup}
        onClose={closeRemoveUserPopup}
        title="Confirm Removing User"
        onConfirm={onConfirmDeleteUser}
        loading={popupLoading}
        message={`Are you sure you want to remove ${removeUserPopup?.firstName} with email ${removeUserPopup?.email}`}
      />
      <ConfirmationPopup
        open={!!removeInvitePopup}
        onClose={closeRemoveInvitePopup}
        title="Confirm Removing User"
        onConfirm={onConfirmDeleteInvite}
        loading={popupLoading}
        message={`Are you sure you want to remove ${removeInvitePopup?.firstName} with email ${removeInvitePopup?.email}`}
      />
    </>
  );
}
